// @ts-nocheck
import React, { useState, useEffect, useCallback } from 'react';
import {
  Package, Plus, Search, Edit2, Trash2, X,
  AlertTriangle, CheckCircle2, ArrowUpDown,
} from 'lucide-react';
import api from '../utils/api';

const inventoryCategories = ['All', 'Raw Materials', 'Finished Goods', 'Packaging'];

const Inventory = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  const emptyForm = { name: '', category: 'Raw Materials', type: '', quantity: '', unit: '', reorderPoint: '', costPerUnit: '', supplier: '', location: '' };
  const [form, setForm] = useState(emptyForm);

  const fetchItems = useCallback(async () => {
    try {
      const res = await api.get('/inventory/');
      // Handle paginated response - extract items array
      setItems(res.data.items || res.data);
    } catch (err) {
      console.error('Failed to fetch inventory', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const filtered = items
    .filter((i) => category === 'All' || i.category === category)
    .filter((i) => i.name.toLowerCase().includes(search.toLowerCase()) || i.type.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      const aVal = a[sortField], bVal = b[sortField];
      const cmp = typeof aVal === 'number' ? aVal - bVal : String(aVal).localeCompare(String(bVal));
      return sortDir === 'asc' ? cmp : -cmp;
    });

  const openAdd = () => { setEditItem(null); setForm(emptyForm); setShowModal(true); };
  const openEdit = (item) => {
    setEditItem(item);
    setForm({ name: item.name, category: item.category, type: item.type, quantity: item.quantity, unit: item.unit, reorderPoint: item.reorderPoint, costPerUnit: item.costPerUnit, supplier: item.supplier || '', location: item.location || '' });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const payload = { ...form, quantity: Number(form.quantity), reorderPoint: Number(form.reorderPoint), costPerUnit: Number(form.costPerUnit) };
    try {
      if (editItem) {
        await api.put(`/inventory/${editItem.id}`, payload);
      } else {
        await api.post('/inventory/', payload);
      }
      fetchItems();
      setShowModal(false);
    } catch (err) {
      console.error('Save failed', err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await api.delete(`/inventory/${id}`);
        fetchItems();
      } catch (err) {
        console.error('Delete failed', err);
      }
    }
  };

  const lowStockCount = items.filter((i) => i.status === 'low').length;
  const totalValue = items.reduce((sum, i) => sum + (i.quantity || 0) * (i.costPerUnit || 0), 0);

  if (loading) {
    return <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0891B2]"></div></div>;
  }

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Inventory</h1>
          <p className="text-sm text-gray-500 mt-1">{items.length} items &bull; Total value: ${totalValue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</p>
        </div>
        <button onClick={openAdd} className="flex items-center gap-2 px-4 py-2.5 bg-[#0891B2] text-white text-sm font-semibold rounded-lg hover:bg-[#0e7490] transition-colors">
          <Plus size={16} /> Add Item
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-cyan-50 flex items-center justify-center"><Package size={20} className="text-[#0891B2]" /></div>
          <div><p className="text-xl font-bold text-gray-900">{items.length}</p><p className="text-xs text-gray-500">Total Items</p></div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center"><AlertTriangle size={20} className="text-amber-500" /></div>
          <div><p className="text-xl font-bold text-gray-900">{lowStockCount}</p><p className="text-xs text-gray-500">Low Stock Items</p></div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center"><CheckCircle2 size={20} className="text-green-500" /></div>
          <div><p className="text-xl font-bold text-gray-900">{items.length - lowStockCount}</p><p className="text-xs text-gray-500">Healthy Stock</p></div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 mb-6">
        <div className="p-4 space-y-3">
          {/* Search */}
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search items..." className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#0891B2] focus:border-transparent outline-none" />
          </div>
          {/* Category filters - horizontal scroll on mobile */}
          <div className="overflow-x-auto -mx-4 px-4">
            <div className="flex gap-2 min-w-max">
              {inventoryCategories.map((cat) => (
                <button key={cat} onClick={() => setCategory(cat)} className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                  category === cat ? 'bg-[#0891B2] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}>{cat}</button>
              ))}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full ekos-table min-w-[800px]">
            <thead>
              <tr className="bg-gray-50">
                <th className="cursor-pointer" onClick={() => handleSort('name')}><span className="flex items-center gap-1">Name <ArrowUpDown size={12} /></span></th>
                <th>Category</th>
                <th>Type</th>
                <th className="cursor-pointer" onClick={() => handleSort('quantity')}><span className="flex items-center gap-1">Qty <ArrowUpDown size={12} /></span></th>
                <th>Unit</th>
                <th>Reorder Pt</th>
                <th className="cursor-pointer" onClick={() => handleSort('costPerUnit')}><span className="flex items-center gap-1">Cost/Unit <ArrowUpDown size={12} /></span></th>
                <th>Supplier</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((item) => (
                <tr key={item.id}>
                  <td className="font-medium text-gray-900">{item.name}</td>
                  <td>{item.category}</td>
                  <td>{item.type}</td>
                  <td className={item.status === 'low' ? 'text-amber-600 font-semibold' : ''}>{item.quantity}</td>
                  <td>{item.unit}</td>
                  <td>{item.reorderPoint}</td>
                  <td>${(item.costPerUnit || 0).toFixed(2)}</td>
                  <td className="text-xs">{item.supplier}</td>
                  <td>
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${item.status === 'low' ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700'}`}>
                      {item.status === 'low' ? 'Low' : 'Good'}
                    </span>
                  </td>
                  <td>
                    <div className="flex gap-1">
                      <button onClick={() => openEdit(item)} className="p-1.5 text-gray-400 hover:text-[#0891B2] hover:bg-cyan-50 rounded transition-colors"><Edit2 size={14} /></button>
                      <button onClick={() => handleDelete(item.id)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={10} className="text-center py-8 text-gray-400">No items found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 animate-fade-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">{editItem ? 'Edit Item' : 'Add New Item'}</h3>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-gray-100 rounded"><X size={20} className="text-gray-400" /></button>
            </div>
            <form onSubmit={handleSave} className="p-5 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="col-span-2"><label className="block text-sm font-medium text-gray-700 mb-1">Name</label><input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0891B2] focus:border-transparent outline-none" required /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Category</label><select value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0891B2] focus:border-transparent outline-none">
                  <option>Raw Materials</option><option>Finished Goods</option><option>Packaging</option>
                </select></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Type</label><input value={form.type} onChange={(e) => setForm({...form, type: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0891B2] focus:border-transparent outline-none" required /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label><input type="number" value={form.quantity} onChange={(e) => setForm({...form, quantity: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0891B2] focus:border-transparent outline-none" required /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Unit</label><input value={form.unit} onChange={(e) => setForm({...form, unit: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0891B2] focus:border-transparent outline-none" required placeholder="lbs, cases, units" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Reorder Point</label><input type="number" value={form.reorderPoint} onChange={(e) => setForm({...form, reorderPoint: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0891B2] focus:border-transparent outline-none" required /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Cost per Unit ($)</label><input type="number" step="0.01" value={form.costPerUnit} onChange={(e) => setForm({...form, costPerUnit: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0891B2] focus:border-transparent outline-none" required /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Supplier</label><input value={form.supplier} onChange={(e) => setForm({...form, supplier: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0891B2] focus:border-transparent outline-none" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Location</label><input value={form.location} onChange={(e) => setForm({...form, location: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0891B2] focus:border-transparent outline-none" /></div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">Cancel</button>
                <button type="submit" className="px-4 py-2 text-sm font-semibold text-white bg-[#0891B2] rounded-lg hover:bg-[#0e7490] transition-colors">{editItem ? 'Save Changes' : 'Add Item'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Inventory;
